/**
 * KioskSequenceDetector
 *
 * Detector cross-platform de sequências de teclas pra desbloqueio do kiosk.
 *
 * <p>Decisão Renato 2026-06-01 — TV Samsung tem múltiplos modelos de controle
 * (do Smart Remote minimalista ao One Remote completo). Pra equipe de campo
 * desbloquear kiosk sem documentação prévia, suportamos 2 combinações
 * universais que cobrem qualquer modelo de controle:</p>
 *
 * <ul>
 *   <li><b>Combo 1</b>: VOL+ × 3 → VOL- × 3 → OK (em até 5s) — funciona em
 *       QUALQUER controle Samsung (vol e OK existem em todos)</li>
 *   <li><b>Combo 2</b>: INFO × 7 (em até 3s) — pra controles que têm INFO,
 *       continuidade do "7-tap" do touch</li>
 * </ul>
 *
 * <p>Cross-platform: o detector é JS puro, sem dependência de Tizen/Android.
 * Listener `keydown` no DOM funciona em TV (registerKey), tablet (USB keyboard
 * de teste), e browser (dev). Cada plataforma pode mapear suas teclas físicas
 * pros `KeyboardEvent.key` aqui esperados (vide {@link normalizeKey}).</p>
 *
 * <p>Não-side-effect: detector apenas dispara callback. Lógica de
 * abrir-keyboard-PIN / unlock-direto continua no caller (KioskGuard).</p>
 */

export interface KioskSequence {
  /**
   * Lista de identificadores de teclas. Comparado com a key normalizada via
   * {@link normalizeKey}. Ex: ['VolumeUp', 'VolumeUp', 'VolumeUp', 'VolumeDown',
   * 'VolumeDown', 'VolumeDown', 'Enter'].
   */
  keys: string[];

  /**
   * Tempo máximo entre teclas consecutivas pra considerar parte da mesma
   * sequência. Acima disso, buffer reseta. Em ms.
   */
  maxIntervalMs: number;

  /** Label humano pra debug/audit (ex: "VOL+/-/OK universal"). */
  label: string;
}

/**
 * Normaliza `KeyboardEvent.key` (ou aliases de plataforma) pro vocabulário
 * canônico das sequências.
 *
 * Tizen TV: `VolumeUp` / `VolumeDown` / `Info` / `Enter` (já standard W3C)
 * Browser dev: idem (teclado padrão emite os mesmos keys)
 * Aliases: alguns controles antigos podem emitir `MediaVolumeUp` / `Numpad+`
 * — mapeamos pro nome canônico.
 */
const KEY_ALIASES: Record<string, string> = {
  // Volume (alguns Tizen antigos emitem variações)
  MediaVolumeUp: 'VolumeUp',
  MediaVolumeDown: 'VolumeDown',
  AudioVolumeUp: 'VolumeUp',
  AudioVolumeDown: 'VolumeDown',
  // Enter/OK (controle Samsung emite "Enter" no botão central)
  Return: 'Enter',
  // Info (Tizen Samsung)
  ContextMenu: 'Info', // alguns modelos
};

export function normalizeKey(rawKey: string): string {
  return KEY_ALIASES[rawKey] ?? rawKey;
}

/**
 * Detector stateful — mantém buffer ring por sequência registrada e dispara
 * callback no primeiro match. Sem dependência de timer global; usa apenas
 * timestamps relativos por keypress.
 */
export class KioskSequenceDetector {
  private readonly sequences: KioskSequence[];
  private readonly onMatch: (sequenceLabel: string) => void;

  /** Buffer de teclas recentes — array de {key, ts}. */
  private buffer: Array<{ key: string; ts: number }> = [];

  /**
   * Bound listener salvo pra remoção idempotente. Criado em {@link attach}.
   */
  private listener: ((e: KeyboardEvent) => void) | null = null;

  constructor(
    sequences: KioskSequence[],
    onMatch: (sequenceLabel: string) => void,
  ) {
    if (!sequences.length) {
      throw new Error('KioskSequenceDetector: pelo menos 1 sequência');
    }
    this.sequences = sequences;
    this.onMatch = onMatch;
  }

  /**
   * Processa uma tecla. Chame manualmente OU use {@link attach} pra
   * registrar listener global no window.
   */
  handleKey(rawKey: string, now: number = performance.now()): void {
    const key = normalizeKey(rawKey);
    this.buffer.push({ key, ts: now });

    // Pruning: remove teclas antigas do buffer (mais velhas que o maior
    // maxIntervalMs * maxLength de qualquer sequência). Evita unbounded growth.
    const maxWindowMs = Math.max(
      ...this.sequences.map((s) => s.maxIntervalMs * s.keys.length),
    );
    this.buffer = this.buffer.filter((entry) => now - entry.ts <= maxWindowMs);

    // Checa cada sequência: pega os últimos N (= keys.length) keys do buffer
    // e valida key-a-key + intervalo entre cada par consecutivo.
    for (const seq of this.sequences) {
      if (this.buffer.length < seq.keys.length) continue;
      const tail = this.buffer.slice(-seq.keys.length);

      // Match das keys?
      const keysMatch = tail.every((entry, i) => entry.key === seq.keys[i]);
      if (!keysMatch) continue;

      // Intervalo entre teclas consecutivas dentro do limite?
      let intervalsOk = true;
      for (let i = 1; i < tail.length; i++) {
        if (tail[i].ts - tail[i - 1].ts > seq.maxIntervalMs) {
          intervalsOk = false;
          break;
        }
      }
      if (!intervalsOk) continue;

      // Match completo! Limpa buffer pra evitar re-trigger acidental e dispara.
      this.buffer = [];
      this.onMatch(seq.label);
      return;
    }
  }

  /**
   * Instala listener `keydown` no window em **capture phase**. Idempotente.
   *
   * <p>Capture phase é crítico: em Tizen, o `lockTizenKioskKeys` instala um
   * blocker também em capture phase que faz `preventDefault` +
   * `stopPropagation` em TODAS as teclas durante kiosk locked. Pra que o
   * detector continue funcionando ANTES do blocker bloquear, o KioskGuard
   * registra o detector PRIMEIRO (mesma phase, ordem de registro determina
   * ordem de execução).</p>
   *
   * <p>Não usamos `stopImmediatePropagation` aqui — só `addEventListener`
   * em capture. O blocker no listener seguinte pode chamar preventDefault
   * (que impede default action) e stopPropagation (que impede listeners
   * em bubble phase), e ainda assim o detector já processou.</p>
   */
  attach(): () => void {
    if (this.listener) return () => this.detach();
    this.listener = (e: KeyboardEvent) => {
      this.handleKey(e.key);
    };
    window.addEventListener('keydown', this.listener, true);
    return () => this.detach();
  }

  detach(): void {
    if (this.listener) {
      window.removeEventListener('keydown', this.listener, true);
      this.listener = null;
    }
    this.buffer = [];
  }

  /** Reset manual do buffer (ex: ao trocar de contexto). */
  reset(): void {
    this.buffer = [];
  }
}

/**
 * Sequências oficiais Efflux pra desbloqueio em TV/teclado.
 * Importadas pelo KioskGuard. Touch (tablet) usa SecretArea — não passa por
 * aqui.
 *
 * <p>v1.0.107 — Combo VOL+/-/OK trocada por ↑×3 → ↓×3 → OK após teste em
 * prod 2026-06-01 (TV UN50U8600FGXZD Consumer): teclas VolumeUp/VolumeDown
 * em Tizen Consumer NÃO chegam ao WebView (Tizen suprime pra ir direto ao
 * tvaudiocontrol nativo + overlay visual). Setas Up/Down chegam normalmente
 * em todas as versões Tizen e em todos os modelos de controle Samsung
 * (Smart Remote minimalista, One Remote, controles de TV mais antigos).</p>
 */
export const EFFLUX_KIOSK_SEQUENCES: KioskSequence[] = [
  {
    label: 'arrow-ok-universal',
    keys: [
      'ArrowUp',
      'ArrowUp',
      'ArrowUp',
      'ArrowDown',
      'ArrowDown',
      'ArrowDown',
      'Enter',
    ],
    maxIntervalMs: 5000,
  },
  /**
   * Combo NUMÉRICO 1→2→3→4→5→6 (em até 5s). Principal pra SSSP: o botão OK/central
   * dessa família vira "mudo" (confuso/ruim de usar) e volume/mudo é suprimido; os
   * NÚMEROS são inequívocos e existem em todo controle. Exige que as teclas 1-6
   * sejam registradas via tvinputdevice.registerKey (vide KIOSK_KEYS_TO_REGISTER em
   * tizenKiosk.ts) — sem isso o firmware SSSP as suprime e não chegam ao WebView.
   */
  {
    label: 'numeric-123456',
    keys: ['1', '2', '3', '4', '5', '6'],
    maxIntervalMs: 5000,
  },
  {
    label: 'info-x7',
    keys: ['Info', 'Info', 'Info', 'Info', 'Info', 'Info', 'Info'],
    maxIntervalMs: 3000,
  },
  /**
   * v1.0.110 (Onda 1.2 Sprint K-Pre-Cert) — Combo dos botões físicos do
   * PAINEL da TV SSSP. Os botões do gabinete em SSSP emitem teclas Tizen
   * (Source + VolumeDown). Apertar Source 3× seguido de VolumeDown 3× em
   * até 5s aciona unlock. Não-aplicável em Consumer (geralmente só tem
   * POWER no painel — vide L11). Em SSSP sem Partner Cert também não
   * recebemos os eventos pra Source — implementado preventivamente pra
   * quando cert chegar.
   */
  {
    label: 'panel-source-vol-down',
    keys: [
      'Source',
      'Source',
      'Source',
      'VolumeDown',
      'VolumeDown',
      'VolumeDown',
    ],
    maxIntervalMs: 5000,
  },
];
